/**
 * Search Results Page — sort dropdown + card reordering + URL-persisted filters.
 */

export const SearchResults = {
	init() {
		const root = document.querySelector( '[data-search-results-page]' );
		if ( ! root ) return;

		this.forceHeaderSearchVisible();
		this.initSortDropdown( root );
		this.initSort( root );
		this.initFiltersModal( root );
		this.initPriceDropdowns();
		this.initToolbarScroll( root );
	},

	// ─── URL helpers ───
	_getParams() {
		return new URLSearchParams( window.location.search );
	},

	_updateURL( params ) {
		const url = window.location.pathname + '?' + params.toString();
		window.history.replaceState( null, '', url );
	},

	// Toggle compact toolbar (hide count) when toolbar becomes sticky.
	// Insert a zero-height sentinel just before the toolbar; when it scrolls
	// out of view the toolbar must be stuck.
	initToolbarScroll( root ) {
		const toolbar = root.querySelector( '.search-results__toolbar' );
		if ( ! toolbar ) return;

		const sentinel = document.createElement( 'div' );
		sentinel.style.height = '0';
		sentinel.style.width = '100%';
		sentinel.setAttribute( 'aria-hidden', 'true' );
		toolbar.parentNode.insertBefore( sentinel, toolbar );

		const observer = new IntersectionObserver(
			( [ entry ] ) => {
				toolbar.classList.toggle( 'is-scrolled', ! entry.isIntersecting );
			},
			{ threshold: [ 0 ] }
		);
		observer.observe( sentinel );
	},

	// On the search results page the header search bar is always visible —
	// update ARIA state and add the classes sticky-search would normally toggle.
	// Also bump --header-h to include the header-search height (it's
	// absolutely positioned so it doesn't contribute to .site-header's
	// offsetHeight on its own), so sticky children sit below the full header.
	forceHeaderSearchVisible() {
		const headerSearch = document.querySelector( '[data-header-search]' );
		const header       = document.querySelector( '.site-header' );
		if ( headerSearch ) {
			headerSearch.classList.add( 'is-visible' );
			headerSearch.setAttribute( 'aria-hidden', 'false' );
		}

		// Use header's rect.bottom plus header-search's layout height
		// (header-search is absolute; it doesn't contribute to header's
		// offsetHeight). rect.bottom is viewport-relative so it includes
		// the wp-admin-bar's 32/46px offset when present — subtract it
		// so --header-h is the pure header size that #main-content should
		// reserve via padding-top (html already has margin-top for the
		// admin bar).
		const updateHeaderH = () => {
			if ( ! header ) return;
			let headerBottom = header.getBoundingClientRect().bottom;
			const adminBar   = document.getElementById( 'wpadminbar' );
			if ( adminBar && adminBar.offsetHeight ) {
				headerBottom = Math.max( 0, headerBottom - adminBar.offsetHeight );
			}
			const total = Math.round( headerBottom + ( headerSearch ? headerSearch.offsetHeight : 0 ) );
			// On search-results the search bar is always visible, so both
			// --header-h and --header-full include it.
			document.documentElement.style.setProperty( '--header-h', total + 'px' );
			document.documentElement.style.setProperty( '--header-full', total + 'px' );
		};
		updateHeaderH();
		window.addEventListener( 'resize', updateHeaderH );
		window.addEventListener( 'load', updateHeaderH );
		if ( document.fonts && document.fonts.ready ) {
			document.fonts.ready.then( updateHeaderH );
		}
		if ( window.ResizeObserver && header ) {
			new ResizeObserver( updateHeaderH ).observe( header );
			if ( headerSearch ) new ResizeObserver( updateHeaderH ).observe( headerSearch );
		}
		if ( headerSearch ) {
			headerSearch.addEventListener( 'transitionend', updateHeaderH );
		}
	},

	initSortDropdown( root ) {
		const dropdown = root.querySelector( '.sort-dropdown' );
		if ( ! dropdown ) return;

		const trigger = dropdown.querySelector( 'button[aria-expanded]' );
		const panel = dropdown.querySelector( '.sort-dropdown__panel' );
		if ( ! trigger || ! panel ) return;

		// Remove hidden attr so CSS transition works; start closed via CSS.
		panel.removeAttribute( 'hidden' );

		const close = () => {
			trigger.setAttribute( 'aria-expanded', 'false' );
			panel.classList.remove( 'is-open' );
		};
		const open = () => {
			trigger.setAttribute( 'aria-expanded', 'true' );
			panel.classList.add( 'is-open' );
		};

		trigger.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			const isOpen = trigger.getAttribute( 'aria-expanded' ) === 'true';
			if ( isOpen ) close();
			else open();
		} );

		document.addEventListener( 'click', ( e ) => {
			if ( ! dropdown.contains( e.target ) ) close();
		} );

		panel.querySelectorAll( 'button' ).forEach( ( option ) => {
			option.addEventListener( 'click', () => {
				const triggerText = trigger.querySelector( 'span' );
				if ( triggerText ) {
					triggerText.textContent = 'Sort: ' + option.textContent.trim();
				}
				panel.querySelectorAll( 'button' ).forEach( ( o ) => o.classList.remove( 'is-active' ) );
				option.classList.add( 'is-active' );
				close();
			} );
		} );
	},

	initSort( root ) {
		const sortDropdown = root.querySelector( '[data-sort]' );
		const list = root.querySelector( '.search-results__list' );
		if ( ! sortDropdown || ! list ) return;

		const doSort = ( value ) => {
			const cards = Array.from( list.querySelectorAll( '.room-card' ) );
			cards.sort( ( a, b ) => {
				const pa = parseFloat( a.dataset.price ) || 0;
				const pb = parseFloat( b.dataset.price ) || 0;
				if ( value === 'price_asc' ) {
					if ( pa === 0 ) return 1;
					if ( pb === 0 ) return -1;
					return pa - pb;
				}
				if ( value === 'price_desc' ) return pb - pa;
				return 0;
			} );
			cards.forEach( ( card ) => list.appendChild( card ) );
		};

		sortDropdown.addEventListener( 'click', ( e ) => {
			const option = e.target.closest( '.sort-dropdown__option' );
			if ( ! option ) return;
			const value = option.dataset.value;
			doSort( value );
			// Persist sort to URL.
			const params = this._getParams();
			if ( value && value !== 'price_asc' ) {
				params.set( 'sort', value );
			} else {
				params.delete( 'sort' );
			}
			this._updateURL( params );
		} );

		// Restore sort from URL.
		const saved = this._getParams().get( 'sort' );
		if ( saved ) {
			const option = sortDropdown.querySelector( `.sort-dropdown__option[data-value="${ saved }"]` );
			if ( option ) {
				sortDropdown.querySelectorAll( '.sort-dropdown__option' ).forEach( ( o ) => o.classList.remove( 'is-active' ) );
				option.classList.add( 'is-active' );
				const triggerText = sortDropdown.querySelector( '.sort-dropdown__trigger span' );
				if ( triggerText ) triggerText.textContent = 'Sort: ' + option.textContent.trim();
				doSort( saved );
			}
		}
	},

	// ─── Price-select dropdown toggles (Min./Max.) ───
	initPriceDropdowns() {
		document.querySelectorAll( '[data-search-filters] .price-select' ).forEach( ( sel ) => {
			const trigger = sel.querySelector( '.price-select__trigger' );
			const panel   = sel.querySelector( '.price-select__panel' );
			if ( ! trigger || ! panel ) return;
			panel.removeAttribute( 'hidden' );

			const close = () => {
				trigger.setAttribute( 'aria-expanded', 'false' );
				panel.classList.remove( 'is-open' );
			};
			const open = () => {
				trigger.setAttribute( 'aria-expanded', 'true' );
				panel.classList.add( 'is-open' );
			};

			trigger.addEventListener( 'click', ( e ) => {
				e.stopPropagation();
				const isOpen = trigger.getAttribute( 'aria-expanded' ) === 'true';
				document.querySelectorAll( '[data-search-filters] .price-select' ).forEach( ( other ) => {
					if ( other === sel ) return;
					const t = other.querySelector( '.price-select__trigger' );
					const p = other.querySelector( '.price-select__panel' );
					if ( t ) t.setAttribute( 'aria-expanded', 'false' );
					if ( p ) p.classList.remove( 'is-open' );
				} );
				if ( isOpen ) close();
				else open();
			} );
			document.addEventListener( 'click', ( e ) => {
				if ( ! sel.contains( e.target ) ) close();
			} );
		} );
	},

	// ─── Filter modal: open/close, checkboxes, price sliders, apply/clear ───
	initFiltersModal( root ) {
		const modal = document.querySelector( '[data-filters-modal][data-search-filters]' );
		if ( ! modal ) return;

		const list       = root.querySelector( '.search-results__list' );
		const countEl    = root.querySelector( '.search-results__count' );
		const openBtns   = document.querySelectorAll( '[data-filters-open]' );
		const closeBtns  = modal.querySelectorAll( '[data-filters-close]' );
		const clearBtn   = modal.querySelector( '[data-filters-clear]' );
		const applyBtn   = modal.querySelector( '[data-filters-apply]' );
		const checkboxes = modal.querySelectorAll( '.filters-checkbox' );

		const rangeMin = modal.querySelector( '.price-chart__range--min' );
		const rangeMax = modal.querySelector( '.price-chart__range--max' );
		const bars     = modal.querySelectorAll( '.price-chart__bar' );
		const step     = rangeMin ? parseFloat( rangeMin.step ) || 500 : 500;

		const updateBars = () => {
			if ( ! rangeMin || ! rangeMax ) return;
			const lo = parseFloat( rangeMin.value );
			const hi = parseFloat( rangeMax.value );
			bars.forEach( ( bar ) => {
				const bucket = parseFloat( bar.dataset.bucket );
				bar.classList.toggle( 'is-outside', bucket < lo || bucket > hi );
			} );
		};

		const syncSelectText = ( type, value ) => {
			const select = modal.querySelector( `[data-price-select="${ type }"] .price-select__trigger span` );
			if ( select ) select.textContent = 'R' + Number( value ).toLocaleString();
		};

		const getFilterState = () => {
			const activeAreas = [];
			checkboxes.forEach( ( cb ) => {
				if ( cb.classList.contains( 'is-checked' ) ) {
					activeAreas.push( cb.dataset.filterArea );
				}
			} );
			let minPrice = 0;
			let maxPrice = Infinity;
			if ( rangeMin && rangeMax ) {
				minPrice = parseFloat( rangeMin.value );
				maxPrice = parseFloat( rangeMax.value );
				if ( minPrice <= parseFloat( rangeMin.min ) && maxPrice >= parseFloat( rangeMax.max ) ) {
					minPrice = 0;
					maxPrice = Infinity;
				}
			}
			return { activeAreas, minPrice, maxPrice };
		};

		const countMatching = () => {
			if ( ! list ) return 0;
			const { activeAreas, minPrice, maxPrice } = getFilterState();
			let n = 0;
			list.querySelectorAll( '.room-card' ).forEach( ( card ) => {
				const sub = card.dataset.suburb;
				const pr  = parseFloat( card.dataset.price ) || 0;
				const areaOk  = activeAreas.length === 0 || activeAreas.includes( sub );
				const priceOk = pr >= minPrice && pr <= maxPrice;
				if ( areaOk && priceOk ) n++;
			} );
			return n;
		};

		const updateApplyCount = () => {
			if ( ! applyBtn ) return;
			const n = countMatching();
			applyBtn.textContent = 'Show results';
		};

		// Open/close.
		openBtns.forEach( ( btn ) => {
			btn.addEventListener( 'click', () => {
				modal.classList.add( 'is-open' );
				modal.setAttribute( 'aria-hidden', 'false' );
				document.body.style.overflow = 'hidden';
				document.body.classList.add( 'filters-open' );
				updateApplyCount();
			} );
		} );
		const closeModal = () => {
			modal.classList.remove( 'is-open' );
			modal.setAttribute( 'aria-hidden', 'true' );
			document.body.style.overflow = '';
			document.body.classList.remove( 'filters-open' );
		};
		closeBtns.forEach( ( btn ) => btn.addEventListener( 'click', closeModal ) );
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && modal.classList.contains( 'is-open' ) ) closeModal();
		} );

		// Checkbox toggle.
		checkboxes.forEach( ( cb ) => {
			cb.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				cb.classList.toggle( 'is-checked' );
				updateApplyCount();
			} );
		} );

		// Price sliders.
		if ( rangeMin && rangeMax ) {
			const clampSliders = () => {
				const lo = parseFloat( rangeMin.value );
				const hi = parseFloat( rangeMax.value );
				// Allow min == max so a single price point can be selected
				// (e.g. 7500–7500 to see only rooms at exactly R7 500). Only
				// stop the two thumbs from crossing past each other.
				if ( lo > hi ) {
					rangeMin.value = hi;
				}
				if ( hi < lo ) {
					rangeMax.value = lo;
				}
			};
			const onMinInput = () => {
				clampSliders();
				syncSelectText( 'min', rangeMin.value );
				updateBars();
				updateApplyCount();
			};
			const onMaxInput = () => {
				clampSliders();
				syncSelectText( 'max', rangeMax.value );
				updateBars();
				updateApplyCount();
			};
			rangeMin.addEventListener( 'input', onMinInput );
			rangeMax.addEventListener( 'input', onMaxInput );

			// Price-select options → move sliders.
			modal.querySelectorAll( '[data-price-select="min"] .price-select__option' ).forEach( ( opt ) => {
				opt.addEventListener( 'click', () => {
					rangeMin.value = parseFloat( opt.dataset.value );
					onMinInput();
				} );
			} );
			modal.querySelectorAll( '[data-price-select="max"] .price-select__option' ).forEach( ( opt ) => {
				opt.addEventListener( 'click', () => {
					rangeMax.value = parseFloat( opt.dataset.value );
					onMaxInput();
				} );
			} );

			updateBars();

			// Show actual min/max values in price-select buttons on init.
			syncSelectText( 'min', rangeMin.value );
			syncSelectText( 'max', rangeMax.value );
		}

		// Clear.
		if ( clearBtn ) {
			clearBtn.addEventListener( 'click', () => {
				checkboxes.forEach( ( cb ) => cb.classList.remove( 'is-checked' ) );
				if ( rangeMin && rangeMax ) {
					rangeMin.value = rangeMin.min;
					rangeMax.value = rangeMax.max;
					syncSelectText( 'min', rangeMin.min );
					syncSelectText( 'max', rangeMax.max );
					updateBars();
				}
				// Clear filter params from URL.
				const params = this._getParams();
				params.delete( 'areas' );
				params.delete( 'min_price' );
				params.delete( 'max_price' );
				this._updateURL( params );
				updateApplyCount();
			} );
		}

		// Apply filters to cards and update count + URL.
		const applyFilters = () => {
			if ( ! list ) return;
			const { activeAreas, minPrice, maxPrice } = getFilterState();
			let shown = 0;
			list.querySelectorAll( '.room-card' ).forEach( ( card ) => {
				const sub = card.dataset.suburb;
				const pr  = parseFloat( card.dataset.price ) || 0;
				const areaOk  = activeAreas.length === 0 || activeAreas.includes( sub );
				const priceOk = pr >= minPrice && pr <= maxPrice;
				const match = areaOk && priceOk;
				card.style.display = match ? '' : 'none';
				if ( match ) shown++;
			} );
			if ( countEl ) {
				countEl.textContent = shown + ' result' + ( shown !== 1 ? 's' : '' );
			}

			// Persist to URL.
			const params = this._getParams();
			if ( activeAreas.length ) {
				params.set( 'areas', activeAreas.join( ',' ) );
			} else {
				params.delete( 'areas' );
			}
			if ( rangeMin && rangeMax ) {
				const lo = parseFloat( rangeMin.value );
				const hi = parseFloat( rangeMax.value );
				if ( lo > parseFloat( rangeMin.min ) ) {
					params.set( 'min_price', lo );
				} else {
					params.delete( 'min_price' );
				}
				if ( hi < parseFloat( rangeMax.max ) ) {
					params.set( 'max_price', hi );
				} else {
					params.delete( 'max_price' );
				}
			}
			this._updateURL( params );
		};

		if ( applyBtn ) {
			applyBtn.addEventListener( 'click', () => {
				applyFilters();
				closeModal();
			} );
		}

		// ─── Restore filters from URL on page load ───
		const params = this._getParams();
		const savedAreas = params.get( 'areas' );
		const savedMin   = params.get( 'min_price' );
		const savedMax   = params.get( 'max_price' );
		let hasFilters = false;

		if ( savedAreas ) {
			const areaList = savedAreas.split( ',' );
			checkboxes.forEach( ( cb ) => {
				if ( areaList.includes( cb.dataset.filterArea ) ) {
					cb.classList.add( 'is-checked' );
					hasFilters = true;
				}
			} );
		}
		if ( rangeMin && rangeMax ) {
			if ( savedMin ) {
				rangeMin.value = parseFloat( savedMin );
				syncSelectText( 'min', rangeMin.value );
				hasFilters = true;
			}
			if ( savedMax ) {
				rangeMax.value = parseFloat( savedMax );
				syncSelectText( 'max', rangeMax.value );
				hasFilters = true;
			}
			updateBars();
		}
		if ( hasFilters ) {
			applyFilters();
		}
	},
};
